import type { ColumnState } from 'ag-grid-community'
import LZString from 'lz-string'
import { useCallback, useEffect, useState } from 'react'

import { ExportSectionSelection } from '@/pages/past-assessment/types'
import {
  Control,
  Dataset,
  ExpectedStatusFeedback,
  Prompt,
  Run,
} from '@/pages/settings-page/control-studio-section/types'

export enum LocalStorageKeys {
  reviewClarifyInitiallyOpen = 'reviewClarifyInitiallyOpen',
  loginRedirectPath = 'loginRedirectPath',
  irqValidated = 'irq-validated',
  // Portal: when the vendor kicked off document analysis, per questionnaire id
  analysisStartedAt = 'analysis-started-at',
  // AG Grid column customization
  agGridColumnsInventory = 'ag-grid-columns:inventory-table',
  agGridColumnsOutreachRecipients = 'ag-grid-columns:outreach-recipients-table',
  agGridColumnsEngagements = 'ag-grid-columns:engagements-table',
  agGridColumnsRequests = 'ag-grid-columns:requests-table',
  agGridColumnsAssessments = 'ag-grid-columns:assessments-table',
  // -v4: -v3 was the first version this table actually persisted, and it briefly stored the
  // grouped layout, which restored as a two-column table. Drop those entries.
  agGridColumnsDiscoverySolutions = 'ag-grid-columns:discovery-solutions-table-v4',
  // AG Grid table persistence (filters, chips, sort, grouped), a record keyed by
  // `{userId}_{tenantId}:{tablePrefix}` — see the accessors at the bottom of this file.
  agGridFilterValues = 'ag-grid-filter-values',
  // Sidebar layout (authenticated shell)
  sidebarPinned = 'sidebar-pinned',
  // Emoji picker: per-emoji use counts, for the "Frequently used" row
  emojiUseCounts = 'emoji-use-counts',
  // Assessment PDF export sections, keyed by user id — not tenant-scoped, the
  // selection is a personal preference that follows the user across tenants.
  assessmentExportSections = 'assessment-export-sections',
  // Control Studio (scoped by org_id)
  controlStudioControls = 'control-studio-controls',
  controlStudioPrompts = 'control-studio-prompts',
  controlStudioDatasets = 'control-studio-datasets',
  controlStudioRuns = 'control-studio-runs',
  controlStudioExpectedStatuses = 'control-studio-expected-statuses',
}

type LocalStorageKeyTypes = {
  [LocalStorageKeys.reviewClarifyInitiallyOpen]: Record<string, boolean>
  [LocalStorageKeys.loginRedirectPath]: string
  [LocalStorageKeys.irqValidated]: Record<string, string[]>
  [LocalStorageKeys.analysisStartedAt]: Record<string, number>
  // AG Grid column states
  [LocalStorageKeys.agGridColumnsInventory]: ColumnState[]
  [LocalStorageKeys.agGridColumnsOutreachRecipients]: ColumnState[]
  [LocalStorageKeys.agGridColumnsEngagements]: ColumnState[]
  [LocalStorageKeys.agGridColumnsRequests]: ColumnState[]
  [LocalStorageKeys.agGridColumnsAssessments]: ColumnState[]
  [LocalStorageKeys.agGridColumnsDiscoverySolutions]: ColumnState[]
  [LocalStorageKeys.agGridFilterValues]: Record<string, StoredFilterEntry>
  [LocalStorageKeys.sidebarPinned]: boolean
  [LocalStorageKeys.emojiUseCounts]: Record<string, number>
  [LocalStorageKeys.assessmentExportSections]: Record<string, Partial<ExportSectionSelection>>
  // Control Studio v2 (scoped by org_id)
  [LocalStorageKeys.controlStudioControls]: Control[]
  [LocalStorageKeys.controlStudioPrompts]: Prompt[]
  [LocalStorageKeys.controlStudioDatasets]: Dataset[]
  [LocalStorageKeys.controlStudioRuns]: Run[]
  [LocalStorageKeys.controlStudioExpectedStatuses]: ExpectedStatusFeedback[]
}

/**
 * Raw JSON access to localStorage, used by useLocalStorage and the AG Grid
 * filter persistence accessors below. Writing null removes the entry.
 */
export const readLocalStorageJson = <T>(storageKey: string): T | undefined => {
  try {
    const raw = window.localStorage.getItem(storageKey)
    return raw ? (JSON.parse(raw) as T) : undefined
  } catch {
    return undefined
  }
}

export const writeLocalStorageJson = (storageKey: string, value: unknown | null): void => {
  try {
    if (value === null) {
      window.localStorage.removeItem(storageKey)
    } else {
      window.localStorage.setItem(storageKey, JSON.stringify(value))
    }
  } catch {
    /* ignore quota / private mode */
  }
}

export const useLocalStorage = <K extends keyof LocalStorageKeyTypes>(
  key: K,
): [LocalStorageKeyTypes[K] | null, (value: LocalStorageKeyTypes[K]) => void, () => void] => {
  const [storedValue, setStoredValue] = useState<LocalStorageKeyTypes[K] | null>(
    () => readLocalStorageJson<LocalStorageKeyTypes[K]>(key) ?? null,
  )

  const setValue = useCallback(
    (value: LocalStorageKeyTypes[K]) => {
      setStoredValue(value)
      writeLocalStorageJson(key, value)
    },
    [key],
  )

  const clearValue = useCallback(() => {
    setStoredValue(null)
    writeLocalStorageJson(key, null)
  }, [key])

  return [storedValue, setValue, clearValue]
}

// Scoped localStorage hook for control studio v2 (includes org_id in key)
export const useScopedLocalStorage = <K extends keyof LocalStorageKeyTypes>(
  key: K,
  orgId: string | null,
): [LocalStorageKeyTypes[K] | null, (value: LocalStorageKeyTypes[K]) => void, () => void] => {
  const scopedKey = orgId ? `${key}_${orgId}` : key

  const readValue = useCallback(() => {
    if (!orgId) return null
    try {
      const item = window.localStorage.getItem(scopedKey)
      if (!item) return null

      // Try parsing as JSON first (legacy)
      try {
        return JSON.parse(item) as LocalStorageKeyTypes[K]
      } catch {
        // If parsing fails, try decompressing
        try {
          const decompressed = LZString.decompressFromUTF16(item)
          if (decompressed) {
            return JSON.parse(decompressed) as LocalStorageKeyTypes[K]
          }
        } catch (e) {
          console.error(`Error decompressing scoped localStorage key "${scopedKey}":`, e)
        }
        return null
      }
    } catch (error) {
      console.error(`Error reading scoped localStorage key "${scopedKey}":`, error)
      return null
    }
  }, [scopedKey, orgId])

  const [storedValue, setStoredValue] = useState<LocalStorageKeyTypes[K] | null>(readValue)

  // Re-read from localStorage when scopedKey changes
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setStoredValue(readValue())
  }, [readValue])

  const setValue = useCallback(
    (value: LocalStorageKeyTypes[K]) => {
      if (!orgId) return
      try {
        setStoredValue(value)
        const stringified = JSON.stringify(value)
        const compressed = LZString.compressToUTF16(stringified)
        window.localStorage.setItem(scopedKey, compressed)
        // Dispatch custom event to notify other hooks
        window.dispatchEvent(new CustomEvent('localStorageChange', { detail: { key: scopedKey } }))
      } catch (error) {
        console.error(`Error setting scoped localStorage key "${scopedKey}":`, error)
      }
    },
    [scopedKey, orgId],
  )

  // Listen for changes from other components
  useEffect(() => {
    const handleStorageChange = (e: Event) => {
      const customEvent = e as CustomEvent<{ key: string }>
      if (customEvent.detail.key === scopedKey) {
        setStoredValue(readValue())
      }
    }

    window.addEventListener('localStorageChange', handleStorageChange)
    return () => window.removeEventListener('localStorageChange', handleStorageChange)
  }, [scopedKey, readValue])

  const clearValue = useCallback(() => {
    if (!orgId) return
    try {
      setStoredValue(null)
      window.localStorage.removeItem(scopedKey)
    } catch (error) {
      console.error(`Error clearing scoped localStorage key "${scopedKey}":`, error)
    }
  }, [scopedKey, orgId])

  return [storedValue, setValue, clearValue]
}

/**
 * AG Grid table persistence (filters, chips, sort, grouped), stored as a
 * record under the single agGridFilterValues key, keyed per table by
 * `{userId}_{tenantId}:{tablePrefix}` (`storageKey`, composed by AgDataTable).
 * Column pin/width/hide/order live in the ag-grid-columns:* ColumnState keys.
 * An undefined storageKey makes every function a no-op.
 *
 * The URL always wins on page entry; stored fields are only read when the matching
 * query param is absent (see getStoredSourceParams). Access is imperative rather
 * than via useLocalStorage: writes happen in debounced callbacks, and each one
 * re-reads the record synchronously so concurrent tables never clobber each other.
 */

export type StoredStringField = 'values' | 'sort'

/** What each table persists in the filter blob: filter chips/values, sort, group-by. */
export type StoredFilterEntry = {
  chips?: string[]
  values?: string
  sort?: string
  grouped?: boolean
}

export const composeTableStorageKey = (
  prefix: string | undefined,
  userId: string | undefined,
  orgId: string | undefined,
): string | undefined => {
  if (!prefix || !userId || !orgId) return undefined
  return `${userId}_${orgId}:${prefix}`
}

const readFilterValuesRecord = (): Record<string, StoredFilterEntry> =>
  readLocalStorageJson<Record<string, StoredFilterEntry>>(LocalStorageKeys.agGridFilterValues) ?? {}

const readFilterValuesEntry = (storageKey: string): StoredFilterEntry =>
  readFilterValuesRecord()[storageKey] ?? {}

const entryHasContent = (entry: StoredFilterEntry): boolean =>
  Boolean(entry.values || entry.chips?.length || entry.sort) || entry.grouped !== undefined

const writeFilterValuesEntry = (storageKey: string, entry: StoredFilterEntry): void => {
  const record = readFilterValuesRecord()
  if (entryHasContent(entry)) {
    record[storageKey] = entry
  } else {
    delete record[storageKey]
  }
  writeLocalStorageJson(
    LocalStorageKeys.agGridFilterValues,
    Object.keys(record).length ? record : null,
  )
}

const writeStoredStringField = (
  storageKey: string | undefined,
  field: StoredStringField,
  value: string | null,
): void => {
  if (!storageKey) return
  writeFilterValuesEntry(storageKey, {
    ...readFilterValuesEntry(storageKey),
    [field]: value ?? undefined,
  })
}

export const writeStoredFilterParam = (
  storageKey: string | undefined,
  value: string | null,
): void => writeStoredStringField(storageKey, 'values', value)

export const writeStoredSort = (storageKey: string | undefined, value: string | null): void =>
  writeStoredStringField(storageKey, 'sort', value)

export const writeStoredGrouped = (storageKey: string | undefined, value: boolean | null): void => {
  if (!storageKey) return
  writeFilterValuesEntry(storageKey, {
    ...readFilterValuesEntry(storageKey),
    grouped: value ?? undefined,
  })
}

export const readStoredChips = (storageKey: string | undefined): string[] | undefined => {
  if (!storageKey) return undefined
  return readFilterValuesEntry(storageKey).chips
}

export const writeStoredChips = (storageKey: string | undefined, chips: string[]): void => {
  if (!storageKey) return
  writeFilterValuesEntry(storageKey, {
    ...readFilterValuesEntry(storageKey),
    chips: chips.length ? chips : undefined,
  })
}

/** URL wins; fall back to the stored field when the query string has no matching param. */
export const getStoredSourceParams = (
  storageKey: string | undefined,
  paramKey: string | undefined,
  urlParams: Record<string, string | undefined>,
  field: StoredStringField,
) => {
  if (!paramKey) return { resolved: undefined, params: urlParams }

  const fromUrl = urlParams[paramKey]
  const fromStorage = !fromUrl && storageKey ? readFilterValuesEntry(storageKey)[field] : undefined

  const resolved = fromUrl || fromStorage
  return {
    resolved,
    params: resolved && !fromUrl ? { ...urlParams, [paramKey]: resolved } : urlParams,
  }
}

export const getFilterSourceParams = (
  storageKey: string | undefined,
  filterKey: string | undefined,
  urlParams: Record<string, string | undefined>,
) => getStoredSourceParams(storageKey, filterKey, urlParams, 'values')

export const getGroupedSource = (
  storageKey: string | undefined,
  groupedKey: string | undefined,
  urlParams: Record<string, string | null | undefined>,
): { resolved: boolean | undefined } => {
  if (groupedKey) {
    const fromUrl = urlParams[groupedKey]
    if (fromUrl === 'true') return { resolved: true }
    if (fromUrl === 'false') return { resolved: false }
  }
  if (!storageKey) return { resolved: undefined }
  return { resolved: readFilterValuesEntry(storageKey).grouped }
}
